import type {
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { Paragraph } from "@smartsuite/react-ui/lib";
import type {
  Doc,
  EditorState,
  PillPickerEditorCommands,
} from "@smartsuite/smartdoc";
import { PillPickerEditor } from "@smartsuite/smartdoc";

import { SmartDocEditorDescription_EditorType } from "@smartsuite/automation-engine-api";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { t } from "i18next";
import type { FC, MutableRefObject, ReactNode } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { checkInputValidation } from "../../../../../../../../stores/automation/utils/inputValidation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type { DynamicSection } from "../../../../../../../../types/line";
import { valueConfigToSmartDoc } from "../../../../../../../../utils/conditions-coverter.hook";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
  isSupportedOutput,
} from "../../../../utils";
import { StyledContainerWrapper } from "../../../style";
import { TriggerDropdown } from "../../containers/TriggerDropdown";
import { generalTypes, ssTypes } from "./string-output-types";
import {
  StyledEditorWithHeaderFooter,
  StyledPillPickerEditorWrapper,
  getEditorStyle,
} from "./style";
import {
  EMPTY_SMARTDOC,
  parseSmartDoc,
  renameRemovedPills,
  toValueConfig,
} from "./to-value-config";
import { ActionLabel } from "../../../../../../../../utils/constants";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { isEmptySmartdoc } from "@smartsuite/automation-conditions";
import { usePillTooltip } from "../../../../../../../../utils/make-tooltip.hook";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import { makeAutomationPillAttrs } from "./pill-attrs";

export const SmartDocEditor: FC<{
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  integrations: IntegrationDescription[];
  description?: InputDescription;
  showValidationError: boolean;
  onClickTrash?: () => void;
  type?: SmartDocEditorDescription_EditorType;
  placeholder?: string;
}> = ({
  input,
  onChange,
  trigger,
  actions,
  action,
  integrations,
  description,
  showValidationError,
  onClickTrash,
  type,
  placeholder,
}) => {
  const store = useRootStore();
  const rootLooping = store.automations.candidate.looping;
  const looping = action
    ? (rootLooping?.scopeForAction?.(action.hash) ?? rootLooping)
    : rootLooping;
  const buttonRef = useRef<MutableRefObject<HTMLElement>>();
  const commandsRef = useRef<PillPickerEditorCommands>();
  const shouldRender = useRef(true);
  const isDocEmpty = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
  const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
  const [focus, setFocus] = useState<boolean>(false);
  const [validationError, setValidationError] = useState<string>("");
  const { getFieldMissingErrorMessage } = useFieldMissingValidation();
  const fieldMissingMessage = getFieldMissingErrorMessage(input, action);
  const [actionType, setActionType] = useState<
    | "replace"
    | "clear"
    | "append"
    | "mathAdd"
    | "mathSubtract"
    | "mathMultiply"
    | "mathDivide"
    | undefined
  >(input.operatorConfig?.operator?.oneofKind);
  const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
  const [smartDoc, setSmartDoc] = useState<Doc>();
  const [headerHTML, setHeaderHTML] = useState<string>();
  const [footerHTML, setFooterHTML] = useState<string>();
  const [isRenderEditor, setIsRenderEditor] = useState<boolean>(true);
  const [currentInput, setCurrentInput] = useState<InputCandidate>(input);
  // The invalid-pill "remount" toggles isRenderEditor false -> true on a 0ms
  // timer. Kept in a ref (not cleared per effect re-run) so a re-run triggered
  // by the false->true state change itself can't cancel the pending restore and
  // strand the editor hidden; only unmount clears it (cleanup effect below).
  const rerenderTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const localOnChange = (input: InputCandidate): void => {
    // avoid onChange if input is the same
    if (JSON.stringify(input) !== JSON.stringify(currentInput)) {
      setCurrentInput(input);
      onChange(input);
    }
  };

  const editorWrapperId = `${input.inputId}-editor-wrapper`;

  const { addTooltipToPills, makeTriggerPillTooltip, makeActionPillTooltip } =
    usePillTooltip();

  const { loopingMenu } = useLoopingMenu({
    trigger,
    actions,
    integrations,
    types: generalTypes,
    ssFieldTypes: ssTypes,
    action,
  });

  useEffect(() => {
    setTriggerMenu(
      convertTriggerToDynamicSection(
        trigger,
        integrations,
        generalTypes,
        generalTypes,
        ssTypes,
      ),
    );
    if (action !== undefined) {
      let verifySupportedAction = isSupportedOutput;
      if (
        input?.value?.values.length === 1 &&
        input.value.values[0].value.oneofKind === "smartdocPillsSummary"
      ) {
        verifySupportedAction = () => true;
      }
      setActionMenus(
        convertActionsToDynamicSections(
          actions,
          action,
          integrations,
          generalTypes,
          ssTypes,
          [],
          looping,
          verifySupportedAction,
        ),
      );
    }
  }, [trigger, actions, action, integrations]);

  useEffect(() => {
    if (!input.value) {
      localOnChange({
        ...input,
        value: { values: [] },
      });
    }
  }, [input, onChange]);

  useLayoutEffect(() => {
    if (!R.isNil(input.value) && shouldRender.current) {
      if (
        input.value.values.length === 1 &&
        (input.value.values[0].value.oneofKind === "smartdoc" ||
          input.value.values[0].value.oneofKind === "smartdocPillsSummary")
      ) {
        const strValue =
          input.value.values[0].value.oneofKind === "smartdoc"
            ? input.value.values[0].value.smartdoc
            : input.value.values[0].value.smartdocPillsSummary;
        const { doc, hasInvalidPills } = renameRemovedPills(
          addTooltipToPills(parseSmartDoc(strValue), trigger, actions, action),
          trigger,
          actions,
          action,
          looping,
        );

        setSmartDoc(doc);
        if (hasInvalidPills) {
          // Need to re-render editor to display updated pill label
          setIsRenderEditor(false);
          clearTimeout(rerenderTimeoutRef.current);
          rerenderTimeoutRef.current = setTimeout(() => {
            setIsRenderEditor(true);
          }, 0);
        }
      } else if (input.value.values.length > 0) {
        // NOTE: Backward compatibility support
        const { doc } = valueConfigToSmartDoc(
          input.value,
          trigger,
          actions,
          action,
          loopingMenu?.options,
        );
        setSmartDoc(doc);
      } else {
        setSmartDoc(EMPTY_SMARTDOC);
      }
    }
  }, [input.value, trigger]);

  // Clear the pending invalid-pill remount timer only on unmount, so an effect
  // re-run never cancels an in-flight restore of the editor.
  useEffect(() => () => clearTimeout(rerenderTimeoutRef.current), []);

  useEffect(() => {
    const smartdocEditorDesc =
      description?.editor?.editor?.oneofKind === "smartdoc"
        ? description.editor.editor.smartdoc
        : undefined;
    setHeaderHTML(smartdocEditorDesc?.header);
    setFooterHTML(smartdocEditorDesc?.footer);
    if (showValidationError) {
      if (R.isTruthy(description)) {
        setValidationError(checkInputValidation(description, input));
      }
    } else {
      setValidationError("");
    }
  }, [input, description, showValidationError]);

  useEffect(() => {
    if (actionType === "clear") {
      setIsCleanAction(true);
    } else {
      setIsCleanAction(false);
    }
  }, [actionType]);

  useEffect(() => {
    if (store.inputToFocus?.inputId === input.inputId) {
      const focusTimeoutId = setTimeout(() => {
        const wrapper = document.getElementById(editorWrapperId);
        const editor = wrapper?.querySelector(".ProseMirror");
        // @ts-expect-error
        editor?.focus();
        store.inputToFocus = undefined;
      });
      return () => clearTimeout(focusTimeoutId);
    }
  }, []);

  const changeActionType = (
    type:
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined,
  ): void => {
    setActionType(type);
    if (type === undefined) return;
    setIsOpen(false);

    if (type === "clear") {
      setSmartDoc(EMPTY_SMARTDOC);
    }
    localOnChange({
      ...input,
      value:
        type === "clear"
          ? {
              values: [],
            }
          : input.value,
      operatorConfig: {
        // @ts-expect-error
        operator: {
          oneofKind: type,
          [type]: {},
        },
      },
    });
  };

  const renderEditor = (
    renderInput: InputCandidate,
    operatorType:
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined,
  ): ReactNode => {
    const updateValue = (value: EditorState): void => {
      shouldRender.current = true;
      if (operatorType) {
        localOnChange({
          ...renderInput,
          value: toValueConfig(value, type),
          operatorConfig: {
            // @ts-expect-error
            operator: {
              oneofKind: operatorType,
              [operatorType]: {},
            },
          },
        });
      } else {
        localOnChange({
          ...renderInput,
          value: toValueConfig(value, type),
        });
      }
      setIsOpen(false);
    };

    return (
      <StyledPillPickerEditorWrapper id={editorWrapperId}>
        {smartDoc && isRenderEditor && (
          <PillPickerEditor
            editable={!isCleanAction}
            showAddButton={!isCleanAction}
            mode={
              type === SmartDocEditorDescription_EditorType.SLACK
                ? "slack"
                : "richtext"
            }
            state={getEditorState(focus, isOpen, fieldMissingMessage !== "")}
            isOpen={isOpen}
            onChangeFocused={setFocus}
            className={getEditorStyle(
              validationError,
              Boolean(headerHTML),
              Boolean(footerHTML),
            )}
            initialDoc={smartDoc}
            commandsRef={commandsRef}
            multiline={true}
            heightMode="grow"
            urlLinkMode="with-pills"
            // @ts-expect-error
            buttonRef={buttonRef}
            onButtonClick={() => setIsOpen(true)}
            onStateChange={(value: EditorState) => {
              updateValue(value);
              shouldRender.current = false;
              const isEmpty = isEmptySmartdoc(value.toJSON().doc);
              isDocEmpty.current = isEmpty;
            }}
            placeholder={
              placeholder ?? t("automation-form.string-field-placeholder")
            }
            onFocus={(e) => {
              description?.editor?.hidePopupOnHover !== true &&
                setTimeout(() => {
                  if (isDocEmpty.current) {
                    setIsOpen(true);
                  }
                  // @ts-expect-error
                  e.target?.focus();
                });
            }}
          >
            {({
              buttonRef,
              isOpen,
              onClose,
              addPill: insertPillFromPicker,
            }) => (
              <TriggerDropdown
                isOpen={isOpen}
                onClose={() => {
                  setIsOpen(false);
                  onClose();
                }}
                onCloseAnimationEnd={() => setIsOpen(false)} // fix issue with onClose dont fired  in time on tap for focused input
                addPill={(attrs: PillNodeAttrs) => {
                  const pillAttrs = makeAutomationPillAttrs(attrs, {
                    trigger,
                    actions,
                    action,
                    looping,
                    makeTriggerPillTooltip,
                    makeActionPillTooltip,
                  });
                  insertPillFromPicker(pillAttrs);
                  setIsOpen(false);
                }}
                triggerMenu={triggerMenu}
                actionMenus={actionMenus}
                loopingMenu={loopingMenu ?? null}
                // @ts-expect-error
                buttonRef={buttonRef}
                action={action}
              />
            )}
          </PillPickerEditor>
        )}
        {validationError !== "" && (
          <Paragraph color={"error"} size={"xs"}>
            {validationError}
          </Paragraph>
        )}
        <FieldMissingError message={fieldMissingMessage} />
      </StyledPillPickerEditorWrapper>
    );
  };

  return (
    <StyledContainerWrapper>
      {description !== undefined && (
        <CaptionBlock
          description={description}
          onClickTrash={onClickTrash}
          isOperatorSelect
        >
          {action?.actionReference?.actionId === ActionLabel.updateRecord &&
            description?.operators?.operators !== undefined &&
            description?.operators?.operators.length > 0 && (
              <UpdateValueDropdown
                operatorConfig={input.operatorConfig}
                operatorDescriptions={description?.operators.operators}
                onOpenedChange={() => {}} // eslint-disable-line @typescript-eslint/no-empty-function
                onChange={changeActionType}
                inCaptionBlock
              />
            )}
        </CaptionBlock>
      )}
      {R.isTruthy(headerHTML) || R.isTruthy(footerHTML) ? (
        <StyledEditorWithHeaderFooter key={actionType}>
          {R.isTruthy(headerHTML) && (
            <div
              className={"header-footer-item header-footer-item--header"}
              dangerouslySetInnerHTML={{ __html: headerHTML }}
            />
          )}
          {renderEditor(input, actionType)}
          {R.isTruthy(footerHTML) && (
            <div
              className={"header-footer-item header-footer-item--footer"}
              dangerouslySetInnerHTML={{ __html: footerHTML }}
            />
          )}
        </StyledEditorWithHeaderFooter>
      ) : (
        <div key={actionType}>{renderEditor(input, actionType)}</div>
      )}
    </StyledContainerWrapper>
  );
};

const getEditorState = (
  focus: boolean,
  isOpen: boolean,
  isError?: boolean,
): "active" | "default" | "error" => {
  if (isError) {
    return "error";
  } else if (focus || isOpen) {
    return "active";
  } else {
    return "default";
  }
};
